// ../../../../2026-08-16/are-2/work/PulseSensorPlayground/docs/signal-coach/pulse-webserial-protocol.mjs?v=20260819-single-r6
var PSWS_PREFIX = "PSWS";
var PSWS_VERSIONS = Object.freeze([1, 2]);
function integer(value, name, minimum, maximum) {
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    throw new Error(`invalid ${name}`);
  }
  return value;
}
function optionalInteger(value, name, minimum, maximum) {
  if (value === void 0 || value === null || value === "") return null;
  return integer(Number(value), name, minimum, maximum);
}
function pulseFrame({
  source,
  format,
  signal,
  timestampMs = null,
  sequence = null,
  mode = "LIVE",
  sourceBpm = null,
  sourceIbi = null,
  sourceBeat = null,
  sourceQuality = null,
  sourceState = null,
  version = 0,
  ...extra
}) {
  return {
    source,
    format,
    version,
    mode,
    sequence,
    timestampMs,
    signal: integer(Number(signal), "signal", 0, 65535),
    sourceBpm: optionalInteger(sourceBpm, "source BPM", 0, 240),
    sourceIbi: optionalInteger(sourceIbi, "source IBI", 0, 1e4),
    sourceBeat: sourceBeat === null ? null : integer(Number(sourceBeat), "source beat", 0, 1),
    sourceQuality: optionalInteger(sourceQuality, "source quality", 0, 100),
    sourceState,
    ...extra
  };
}
function parseJsonLine(normalized) {
  if (!normalized.startsWith("{")) return null;
  let value;
  try {
    value = JSON.parse(normalized);
  } catch {
    throw new Error("invalid JSON pulse frame");
  }
  const signal = value.signal ?? value.sample ?? value.raw ?? value.value;
  if (signal === void 0) return null;
  return pulseFrame({
    source: "json",
    format: "JSON pulse sample",
    signal,
    timestampMs: optionalInteger(value.timestampMs ?? value.timestamp ?? value.time, "timestamp", 0, Number.MAX_SAFE_INTEGER),
    sequence: optionalInteger(value.sequence ?? value.seq, "sequence", 0, 2147483647),
    sourceBpm: value.bpm,
    sourceIbi: value.ibi ?? value.ibiMs,
    sourceBeat: value.beat,
    sourceQuality: value.quality,
    sourceState: typeof value.state === "string" ? value.state : null
  });
}
function parsePulseLine(line) {
  const normalized = line.trim();
  if (!normalized) return null;
  const json = parseJsonLine(normalized);
  if (json) return json;
  const fields = normalized.split(",").map((field) => field.trim());
  if (fields[0] === PSWS_PREFIX) {
    const version = integer(Number(fields[1]), "protocol version", 1, 255);
    if (!PSWS_VERSIONS.includes(version)) throw new Error(`unsupported PSWS version ${version}`);
    const expectedFields = version === 1 ? 11 : 18;
    if (fields.length !== expectedFields) throw new Error("invalid PSWS field count");
    const mode = fields[2];
    if (mode !== "LIVE" && mode !== "SIM") throw new Error("invalid PSWS mode");
    const sourceState = fields[10];
    if (!/^[A-Z0-9_]+$/.test(sourceState)) throw new Error("invalid PSWS state");
    const frame = pulseFrame({
      source: "pulselink",
      format: `PulseLink PSWS v${version}`,
      version,
      mode,
      sequence: integer(Number(fields[3]), "sequence", 0, 2147483647),
      timestampMs: integer(Number(fields[4]), "timestamp", 0, 1073741823),
      signal: fields[5],
      sourceBpm: fields[6],
      sourceIbi: fields[7],
      sourceBeat: fields[8],
      sourceQuality: fields[9],
      sourceState
    });
    if (version === 2) {
      Object.assign(frame, {
        sourceThreshold: integer(Number(fields[11]), "source threshold", -1024, 2047),
        sourceSignalMin: integer(Number(fields[12]), "source signal minimum", -1024, 2047),
        sourceSignalMax: integer(Number(fields[13]), "source signal maximum", -1024, 2047),
        battery: integer(Number(fields[14]), "battery", -1, 100),
        charging: Boolean(integer(Number(fields[15]), "charging", 0, 1)),
        linked: Boolean(integer(Number(fields[16]), "linked", 0, 1)),
        sourceLocked: Boolean(integer(Number(fields[17]), "source locked", 0, 1))
      });
    }
    return frame;
  }
  if (fields.length === 4 && fields.every((field) => field !== "" && Number.isFinite(Number(field)))) {
    return pulseFrame({
      source: "arduino-csv",
      format: "Arduino signal,bpm,ibi,beat CSV",
      signal: fields[0],
      sourceBpm: fields[1],
      sourceIbi: fields[2],
      sourceBeat: fields[3]
    });
  }
  if (fields.length === 2 && fields.every((field) => field !== "" && Number.isFinite(Number(field)))) {
    return pulseFrame({
      source: "timestamped-csv",
      format: "timestamp,signal CSV",
      timestampMs: integer(Number(fields[0]), "timestamp", 0, Number.MAX_SAFE_INTEGER),
      signal: fields[1]
    });
  }
  if (/^[-+]?\d+$/.test(normalized)) {
    return pulseFrame({ source: "raw-number", format: "raw numeric samples", signal: normalized });
  }
  const prefixed = normalized.match(/^[Ss]\s*[:=]?\s*(\d+)$/);
  if (prefixed) {
    return pulseFrame({ source: "pulse-serial", format: "PulseSensor S-value samples", signal: prefixed[1] });
  }
  const labeled = normalized.match(/^(?:signal|sig|sample|raw)(?:\s*[:=]\s*|\s+)(\d+)$/i);
  if (labeled) {
    return pulseFrame({ source: "labeled", format: "labeled pulse samples", signal: labeled[1] });
  }
  if (/^[BbQq]\s*[:=]?\s*\d+$/.test(normalized)) return null;
  return null;
}

// ../../../../2026-08-16/are-2/work/PulseSensorPlayground/docs/signal-coach/signal-coach-core.mjs?v=20260819-single-r6
var DEFAULTS = Object.freeze({
  pulseThreshold: 550,
  thresholdMode: "adaptive",
  refractoryMs: 250,
  noBeatTimeoutMs: 3e3,
  staleMs: 1500,
  minBpm: 40,
  maxBpm: 180,
  minIbi: 333,
  maxIbi: 1500,
  minAmplitude: 20,
  qualitySteps: 12,
  qualityLock: 10,
  qualityUp: 3,
  qualityDown: 1,
  qualityFastUp: 6,
  bpmAverageCount: 10,
  rangeSnap: 80,
  rearmRange: 120,
  rearmNoBeatMs: 2200,
  rearmCooldownMs: 3500,
  flatRange: 90,
  flatAmplitude: 12,
  resyncLabelMs: 900,
  resyncFastMs: 6e3,
  beatFlashMs: 200
});
var COACH_COPY = Object.freeze({
  READY: "Connect a pulse-wave stream to begin.",
  NO_SIGNAL: "Check power and the signal wire. Then place the sensor on skin.",
  HOLD_STEADY: "Use light, steady pressure. Do not squeeze the sensor.",
  SEARCHING: "Waveform found. Keep still while Chrome finds the beat pattern.",
  GOOD_WAVE: "Good waveform. Hold this position for a few more beats.",
  LOCKING: "Almost there. Keep your hand relaxed and still.",
  SIGNAL_LOST: "The signal moved. Reposition gently and hold still.",
  QUALIFIED: "Signal looks consistent. Browser BPM is ready.",
  RESYNC: "Chrome retuned to the waveform on screen. Keep still."
});
function clamp(value, minimum, maximum) {
  return Math.max(minimum, Math.min(maximum, value));
}
function nowMs() {
  return globalThis.performance?.now?.() ?? Date.now();
}
var BrowserSignalCoach = class {
  constructor(options = {}) {
    this.options = { ...DEFAULTS, ...options };
    if (!["adaptive", "fixed"].includes(this.options.thresholdMode)) {
      throw new Error("threshold mode must be adaptive or fixed");
    }
    this.reset();
  }
  setThresholdMode(mode, threshold = this.options.pulseThreshold) {
    if (!["adaptive", "fixed"].includes(mode)) throw new Error("threshold mode must be adaptive or fixed");
    if (!Number.isFinite(threshold) || threshold < 0 || threshold > 1023) throw new Error("threshold must be 0..1023");
    this.options.thresholdMode = mode;
    this.options.pulseThreshold = Math.round(threshold);
    this.threshold = this.options.pulseThreshold;
    this.peak = this.threshold;
    this.trough = this.threshold;
    this.pulsing = false;
    return this.lastResult;
  }
  reset(timestamp = null) {
    const start = timestamp ?? 0;
    const initial = this.options.pulseThreshold;
    this.inputDivisor = null;
    this.signal = initial;
    this.minimum = initial - this.options.rangeSnap / 2;
    this.maximum = initial + this.options.rangeSnap / 2;
    this.threshold = initial;
    this.peak = initial;
    this.trough = initial;
    this.pulsing = false;
    this.ibi = 600;
    this.amplitude = 0;
    this.quality = 0;
    this.bpm = 0;
    this.rates = [];
    this.locked = false;
    this.firstBeat = true;
    this.secondBeat = false;
    this.lastBeat = start;
    this.lastQualifiedBeat = null;
    this.lastRearm = start;
    this.rearms = 0;
    this.resyncLabelUntil = -Infinity;
    this.resyncFastUntil = -Infinity;
    this.beatFlashUntil = -Infinity;
    this.sampleCount = 0;
    this.state = "READY";
    this.lastResult = this.snapshot(start, false, false);
    return this.lastResult;
  }
  normalizeSignal(rawSignal) {
    if (!Number.isFinite(rawSignal)) throw new Error("pulse sample must be numeric");
    if (rawSignal < 0 || rawSignal > 65535) throw new Error("pulse sample is outside the supported range");
    if (this.inputDivisor === null) this.inputDivisor = rawSignal > 1300 ? 4 : 1;
    if (rawSignal > 1300) this.inputDivisor = 4;
    return clamp(Math.round(rawSignal / this.inputDivisor), 0, 1023);
  }
  trackRange(signal) {
    this.minimum = Math.min(this.minimum + 1, signal);
    this.maximum = Math.max(this.maximum - 1, signal);
    if (this.maximum - this.minimum < this.options.rangeSnap) {
      this.minimum = signal - this.options.rangeSnap / 2;
      this.maximum = signal + this.options.rangeSnap / 2;
    }
  }
  qualifies(interval) {
    const rate = interval ? Math.floor(6e4 / interval) : 0;
    return rate >= this.options.minBpm && rate <= this.options.maxBpm && interval >= this.options.minIbi && interval <= this.options.maxIbi && this.amplitude >= this.options.minAmplitude;
  }
  smoothedBpm(interval) {
    this.rates.push(interval);
    if (this.rates.length > this.options.bpmAverageCount) this.rates.shift();
    const total = this.rates.reduce((sum, value) => sum + value, 0);
    return Math.floor(6e4 * this.rates.length / total);
  }
  rearm(timestamp) {
    const midpoint = this.options.thresholdMode === "fixed" ? this.options.pulseThreshold : this.maximum > this.minimum ? Math.floor((this.minimum + this.maximum) / 2) : this.options.pulseThreshold;
    this.threshold = midpoint;
    this.peak = midpoint;
    this.trough = midpoint;
    this.pulsing = false;
    this.quality = 0;
    this.bpm = 0;
    this.rates = [];
    this.locked = false;
    this.rearms += 1;
    this.lastRearm = timestamp;
    this.lastBeat = timestamp;
  }
  resync(timestamp = nowMs()) {
    const midpoint = this.options.thresholdMode === "fixed" ? this.options.pulseThreshold : this.maximum > this.minimum ? Math.floor((this.minimum + this.maximum) / 2) : this.options.pulseThreshold;
    this.threshold = midpoint;
    this.peak = midpoint;
    this.trough = midpoint;
    this.pulsing = false;
    this.ibi = 600;
    this.amplitude = Math.max(this.amplitude, this.maximum - this.minimum);
    this.firstBeat = true;
    this.secondBeat = false;
    this.quality = 0;
    this.bpm = 0;
    this.rates = [];
    this.locked = false;
    this.rearms += 1;
    this.lastBeat = timestamp;
    this.lastRearm = timestamp;
    this.lastQualifiedBeat = timestamp;
    this.resyncLabelUntil = timestamp + this.options.resyncLabelMs;
    this.resyncFastUntil = timestamp + this.options.resyncFastMs;
    this.state = "RESYNC";
    this.lastResult = this.snapshot(timestamp, false, false);
    return this.lastResult;
  }
  detect(timestamp) {
    const elapsed = timestamp - this.lastBeat;
    const gate = Math.floor(this.ibi * 3 / 5);
    let candidateBeat = false;
    let qualifiedBeat = false;
    if (this.signal < this.threshold && elapsed > gate && this.signal < this.trough) {
      this.trough = this.signal;
    }
    if (this.signal > this.threshold && this.signal > this.peak) this.peak = this.signal;
    if (elapsed > this.options.refractoryMs && elapsed > gate && this.signal > this.threshold && !this.pulsing) {
      this.pulsing = true;
      candidateBeat = true;
      const interval = elapsed;
      this.lastBeat = timestamp;
      if (this.secondBeat) {
        this.secondBeat = false;
        this.ibi = interval;
      } else if (this.firstBeat) {
        this.firstBeat = false;
        this.secondBeat = true;
      } else {
        this.ibi = interval;
        qualifiedBeat = this.qualifies(interval);
        if (qualifiedBeat) {
          this.bpm = this.smoothedBpm(interval);
          this.lastQualifiedBeat = timestamp;
          const step = timestamp < this.resyncFastUntil ? this.options.qualityFastUp : this.options.qualityUp;
          this.quality = Math.min(this.options.qualitySteps, this.quality + step);
        } else {
          this.quality = Math.max(0, this.quality - this.options.qualityDown);
        }
        this.locked = this.quality >= this.options.qualityLock;
        if (this.locked && qualifiedBeat) this.beatFlashUntil = timestamp + this.options.beatFlashMs;
      }
    }
    if (this.signal < this.threshold && this.pulsing) {
      this.pulsing = false;
      this.amplitude = this.peak - this.trough;
      this.threshold = this.options.thresholdMode === "adaptive" ? this.trough + Math.floor(this.amplitude / 2) : this.options.pulseThreshold;
      this.peak = this.threshold;
      this.trough = this.threshold;
    }
    if (this.lastQualifiedBeat === null || timestamp - this.lastQualifiedBeat > this.options.noBeatTimeoutMs) {
      this.locked = false;
      this.quality = 0;
      this.bpm = 0;
    }
    if (!this.locked && this.maximum - this.minimum >= this.options.rearmRange && timestamp - this.lastBeat >= this.options.rearmNoBeatMs && timestamp - this.lastRearm >= this.options.rearmCooldownMs) {
      this.rearm(timestamp);
    }
    return { candidateBeat, qualifiedBeat };
  }
  coachState(timestamp) {
    const range = this.maximum - this.minimum;
    if (timestamp < this.resyncLabelUntil) return "RESYNC";
    const fresh = this.lastQualifiedBeat !== null && timestamp - this.lastQualifiedBeat <= this.options.staleMs;
    if (fresh && this.locked && this.quality >= this.options.qualitySteps) return "QUALIFIED";
    if (fresh && (this.locked || this.quality > 0)) return "LOCKING";
    if (this.locked) return "SIGNAL_LOST";
    if (range < this.options.flatRange || this.amplitude < this.options.flatAmplitude) return "NO_SIGNAL";
    if (this.amplitude < this.options.minAmplitude) return "HOLD_STEADY";
    if (range >= this.options.rearmRange) return "GOOD_WAVE";
    return "SEARCHING";
  }
  snapshot(timestamp, candidateBeat, qualifiedBeat) {
    return {
      state: this.state,
      quality: Math.round(this.quality),
      locked: this.state === "QUALIFIED",
      bpm: this.state === "QUALIFIED" ? this.bpm : 0,
      ibi: this.state === "QUALIFIED" ? this.ibi : 0,
      beat: this.state === "QUALIFIED" && qualifiedBeat,
      candidateBeat,
      qualifiedBeat,
      amplitude: Math.round(this.amplitude),
      threshold: Math.round(this.threshold),
      thresholdMode: this.options.thresholdMode,
      range: Math.round(this.maximum - this.minimum),
      minimum: Math.round(this.minimum),
      maximum: Math.round(this.maximum),
      signal10: this.signal,
      rearms: this.rearms,
      sampleCount: this.sampleCount,
      timestamp
    };
  }
  update(frameOrSignal, timestamp = nowMs()) {
    const rawSignal = typeof frameOrSignal === "number" ? frameOrSignal : frameOrSignal.signal;
    this.signal = this.normalizeSignal(rawSignal);
    this.sampleCount += 1;
    this.trackRange(this.signal);
    const { candidateBeat, qualifiedBeat } = this.detect(timestamp);
    this.state = this.coachState(timestamp);
    this.lastResult = this.snapshot(timestamp, candidateBeat, qualifiedBeat);
    return this.lastResult;
  }
};
function stateColor(state) {
  if (state === "QUALIFIED") return "green";
  if (state === "LOCKING" || state === "SIGNAL_LOST" || state === "RESYNC") return "yellow";
  return "blue";
}
function adviceFor(state) {
  return COACH_COPY[state] ?? COACH_COPY.SEARCHING;
}

// ../../../../2026-08-16/are-2/work/PulseSensorPlayground/docs/signal-coach/signal-coach.mjs
var pageOptions = new URLSearchParams(location.search);
if (window.self !== window.top || pageOptions.get("embedded") === "1") {
  document.documentElement.classList.add("is-embedded");
}
var HISTORY_LENGTH = 600;
var canvas = document.querySelector("#waveformCanvas");
var context = canvas.getContext("2d");
var display = document.querySelector(".display");
var connectBtn = document.querySelector("#connectBtn");
var resyncBtn = document.querySelector("#resyncBtn");
var sampleRate = document.querySelector("#sampleRate");
var statusDot = document.querySelector("#statusDot");
var statusText = document.querySelector("#statusText");
var sourceMeta = document.querySelector("#sourceMeta");
var sourceName = document.querySelector("#sourceName");
var screenSource = document.querySelector("#screenSource");
var errorMessage = document.querySelector("#errorMessage");
var simulationWarning = document.querySelector("#simulationWarning");
var waitingOverlay = document.querySelector("#waitingOverlay");
var heart = document.querySelector("#heart");
var bpmCard = document.querySelector("#bpmCard");
var liveBpm = document.querySelector("#liveBpm");
var bpmNote = document.querySelector("#bpmNote");
var coachLabel = document.querySelector("#coachLabel");
var coachAdvice = document.querySelector("#coachAdvice");
var qualitySegments = [...document.querySelectorAll("#qualityBar span")];
var modeChip = document.querySelector("#modeChip");
var frameMeta = document.querySelector("#frameMeta");
var signalMeta = document.querySelector("#signalMeta");
var liveSignal = document.querySelector("#liveSignal");
var liveBeat = document.querySelector("#liveBeat");
var liveIbi = document.querySelector("#liveIbi");
var liveQuality = document.querySelector("#liveQuality");
var liveInputFormat = document.querySelector("#liveInputFormat");
var sourceMetrics = document.querySelector("#sourceMetrics");
var rawSerial = document.querySelector("#rawSerial");
var port = null;
var reader = null;
var readTask = null;
var reading = false;
var history = new Array(HISTORY_LENGTH).fill(null);
var frameCount = 0;
var gaps = 0;
var lastSequence = null;
var lastFrame = null;
var lastCoach = { state: "READY", quality: 0, locked: false, range: 0, bpm: 0, ibi: 0, beat: false };
var streamTimestamp = null;
var lastDeviceTimestamp = null;
var resyncUiTimer = null;
var browserCoach = new BrowserSignalCoach();
function resizeCanvas() {
  const rectangle = canvas.getBoundingClientRect();
  if (!rectangle.width || !rectangle.height) return;
  const dpr = window.devicePixelRatio || 1;
  const width = Math.max(1, Math.round(rectangle.width * dpr));
  const height = Math.max(1, Math.round(rectangle.height * dpr));
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
    context.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  drawWaveform();
}
function drawWaveform() {
  const width = canvas.getBoundingClientRect().width;
  const height = canvas.getBoundingClientRect().height;
  if (!width || !height) return;
  context.fillStyle = "#07110f";
  context.fillRect(0, 0, width, height);
  context.strokeStyle = "#25352e";
  context.lineWidth = 1;
  for (let x = 0; x <= width; x += width / 8) {
    context.beginPath();
    context.moveTo(x, 0);
    context.lineTo(x, height);
    context.stroke();
  }
  for (let y = 0; y <= height; y += height / 4) {
    context.beginPath();
    context.moveTo(0, y);
    context.lineTo(width, y);
    context.stroke();
  }
  const values = history.filter((value) => value !== null);
  if (values.length < 2) return;
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const padding = Math.max(35, (maximum - minimum) * 0.13);
  const low = minimum - padding;
  const high = maximum + padding;
  const range = Math.max(1, high - low);
  const color = stateColor(lastCoach.state) === "green" ? "#76f7c2" : stateColor(lastCoach.state) === "yellow" ? "#f0bf52" : "#55d6e3";
  context.strokeStyle = color;
  context.lineWidth = 3;
  context.lineJoin = "round";
  context.beginPath();
  let started = false;
  history.forEach((value, index) => {
    if (value === null) return;
    const x = index / (HISTORY_LENGTH - 1) * width;
    const y = height - (value - low) / range * height;
    if (!started) {
      context.moveTo(x, y);
      started = true;
    } else context.lineTo(x, y);
  });
  context.stroke();
}
function nextSampleTimestamp(frame, explicitTimestamp = null) {
  const period = 1e3 / Number(sampleRate.value);
  if (explicitTimestamp !== null) {
    streamTimestamp = explicitTimestamp;
    return streamTimestamp;
  }
  if (frame.timestampMs !== null) {
    const deviceTimestamp = frame.timestampMs;
    if (lastDeviceTimestamp === null) streamTimestamp = deviceTimestamp;
    else if (deviceTimestamp > lastDeviceTimestamp) streamTimestamp += deviceTimestamp - lastDeviceTimestamp;
    else streamTimestamp += period;
    lastDeviceTimestamp = deviceTimestamp;
    return streamTimestamp;
  }
  streamTimestamp = streamTimestamp === null ? 0 : streamTimestamp + period;
  return streamTimestamp;
}
function senderDiagnostics(frame) {
  const values = [];
  if (frame.sourceBpm !== null) values.push(`BPM ${frame.sourceBpm}`);
  if (frame.sourceIbi !== null) values.push(`IBI ${frame.sourceIbi}`);
  if (frame.sourceQuality !== null) values.push(`Q ${frame.sourceQuality}`);
  return values.length ? values.join(" \xB7 ") : "--";
}
function updateCoach(coach) {
  const color = stateColor(coach.state);
  display.classList.remove("state-blue", "state-yellow", "state-green");
  display.classList.add(`state-${color}`);
  coachLabel.textContent = coach.state.replaceAll("_", " ");
  coachAdvice.textContent = adviceFor(coach.state);
  qualitySegments.forEach((segment, index) => segment.classList.toggle("filled", index < Math.floor(coach.quality * 10 / 12)));
  liveBpm.textContent = coach.locked && coach.bpm > 0 ? String(coach.bpm) : "--";
  bpmNote.textContent = coach.locked ? "Stable signal detected in Chrome" : "Wait for a clean signal";
  liveQuality.textContent = `${coach.quality}/12`;
}
function updateFrame(frame, line, explicitTimestamp = null) {
  const timestamp = nextSampleTimestamp(frame, explicitTimestamp);
  frameCount += 1;
  if (frame.sequence !== null && lastSequence !== null && frame.sequence !== (lastSequence + 1 & 2147483647)) gaps += 1;
  if (frame.sequence !== null) lastSequence = frame.sequence;
  lastFrame = frame;
  lastCoach = browserCoach.update(frame.signal, timestamp);
  history.shift();
  history.push(frame.signal);
  drawWaveform();
  updateCoach(lastCoach);
  const simulated = frame.mode === "SIM";
  waitingOverlay.hidden = true;
  resyncBtn.disabled = false;
  statusDot.className = `status-dot ${simulated ? "simulation" : "connected"}`;
  statusText.textContent = simulated ? "Simulated bench replay" : "Pulse stream connected";
  sourceMeta.textContent = `${frame.format} \xB7 coach runs in Chrome`;
  sourceName.textContent = simulated ? "SIM PULSE" : "PulseSensor";
  screenSource.textContent = "CHROME";
  simulationWarning.hidden = !simulated;
  modeChip.textContent = simulated ? "SIM" : "LIVE";
  modeChip.className = simulated ? "sim" : "live";
  frameMeta.textContent = `${lastCoach.state.replaceAll("_", " ")} \xB7 ${frameCount.toLocaleString()} samples \xB7 ${gaps} gaps`;
  signalMeta.textContent = `Signal ${frame.signal}`;
  liveSignal.textContent = String(frame.signal);
  liveBeat.textContent = lastCoach.beat ? "1" : "0";
  liveIbi.textContent = lastCoach.ibi > 0 ? String(lastCoach.ibi) : "--";
  liveInputFormat.textContent = frame.format;
  sourceMetrics.textContent = senderDiagnostics(frame);
  rawSerial.textContent = line;
  if (lastCoach.beat) {
    heart.classList.add("beat");
    bpmCard.classList.add("invert");
    setTimeout(() => {
      heart.classList.remove("beat");
      bpmCard.classList.remove("invert");
    }, 180);
  }
}
function showError(message) {
  errorMessage.textContent = message;
  errorMessage.hidden = false;
}
function resetCoachState() {
  streamTimestamp = null;
  lastDeviceTimestamp = null;
  browserCoach.reset();
}
function resetDisconnectedDisplay() {
  history = new Array(HISTORY_LENGTH).fill(null);
  frameCount = 0;
  gaps = 0;
  lastSequence = null;
  lastFrame = null;
  lastCoach = { state: "READY", quality: 0, locked: false, range: 0, bpm: 0, ibi: 0, beat: false };
  resetCoachState();
  display.classList.remove("state-yellow", "state-green");
  display.classList.add("state-blue");
  screenSource.textContent = "CHROME";
  waitingOverlay.hidden = false;
  waitingOverlay.textContent = "CONNECT A PULSE STREAM";
  if (resyncUiTimer !== null) clearTimeout(resyncUiTimer);
  resyncUiTimer = null;
  resyncBtn.textContent = "Re-sync Signal";
  resyncBtn.classList.remove("confirmed");
  resyncBtn.disabled = true;
  sampleRate.disabled = false;
  liveBpm.textContent = "--";
  bpmNote.textContent = "Wait for a clean signal";
  coachLabel.textContent = "READY";
  coachAdvice.textContent = adviceFor("READY");
  qualitySegments.forEach((segment) => segment.classList.remove("filled"));
  modeChip.textContent = "WAITING";
  modeChip.className = "";
  frameMeta.textContent = "No samples received";
  signalMeta.textContent = "Signal --";
  liveSignal.textContent = "--";
  liveBeat.textContent = "--";
  liveIbi.textContent = "--";
  liveQuality.textContent = "--";
  liveInputFormat.textContent = "--";
  sourceMetrics.textContent = "--";
  rawSerial.textContent = "waiting for data...";
  simulationWarning.hidden = true;
  drawWaveform();
}
async function readLoop() {
  const decoder = new TextDecoder();
  let buffer = "";
  while (reading && port?.readable) {
    const activeReader = port.readable.getReader();
    reader = activeReader;
    try {
      while (reading) {
        const { value, done } = await activeReader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split(/\r?\n/);
        buffer = lines.pop() ?? "";
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;
          rawSerial.textContent = trimmed;
          try {
            const frame = parsePulseLine(trimmed);
            if (frame) updateFrame(frame, trimmed);
          } catch (error) {
            console.warn("Ignored invalid serial frame:", error.message);
          }
        }
      }
    } finally {
      activeReader.releaseLock();
      if (reader === activeReader) reader = null;
    }
  }
}
async function finalizeConnection(targetPort) {
  if (!targetPort || targetPort !== port) return false;
  reading = false;
  port = null;
  const activeReader = reader;
  const activeReadTask = readTask;
  if (activeReader) {
    try {
      await activeReader.cancel();
    } catch {
    }
  }
  if (activeReadTask) {
    try {
      await activeReadTask;
    } catch {
    }
  }
  if (readTask === activeReadTask) readTask = null;
  try {
    await targetPort.close();
  } catch {
  }
  connectBtn.textContent = "Connect";
  statusDot.className = "status-dot";
  statusText.textContent = "Disconnected";
  sourceMeta.textContent = "Pulse-wave input \xB7 115200 baud";
  resetDisconnectedDisplay();
  return true;
}
async function connect() {
  if (!("serial" in navigator)) {
    showError("Web Serial is unavailable. Use desktop Chrome, Edge, or Brave over HTTPS.");
    return;
  }
  let requestedPort = null;
  try {
    requestedPort = await navigator.serial.requestPort();
    port = requestedPort;
    await requestedPort.open({ baudRate: 115200, bufferSize: 8192 });
    history = new Array(HISTORY_LENGTH).fill(null);
    frameCount = 0;
    gaps = 0;
    lastSequence = null;
    resetCoachState();
    reading = true;
    errorMessage.hidden = true;
    sampleRate.disabled = true;
    connectBtn.textContent = "Disconnect";
    statusText.textContent = "Waiting for samples";
    sourceMeta.textContent = "Connected \xB7 USB serial \xB7 115200 baud";
    waitingOverlay.textContent = "WAITING FOR PULSE DATA";
    readTask = readLoop();
    await readTask;
    if (port === requestedPort) await finalizeConnection(requestedPort);
  } catch (error) {
    if (requestedPort && port === requestedPort) await finalizeConnection(requestedPort);
    if (error.name !== "NotFoundError") showError(`Connection failed: ${error.message}`);
  }
}
connectBtn.addEventListener("click", () => {
  if (port) finalizeConnection(port).catch((error) => showError(error.message));
  else connect().catch((error) => showError(error.message));
});
resyncBtn.addEventListener("click", () => {
  if (streamTimestamp === null) return;
  lastCoach = browserCoach.resync(streamTimestamp);
  updateCoach(lastCoach);
  coachLabel.textContent = "RESYNC";
  resyncBtn.textContent = "Retuned \u2014 keep still";
  resyncBtn.classList.add("confirmed");
  if (resyncUiTimer !== null) clearTimeout(resyncUiTimer);
  resyncUiTimer = setTimeout(() => {
    resyncBtn.textContent = "Re-sync Signal";
    resyncBtn.classList.remove("confirmed");
    resyncUiTimer = null;
  }, 900);
});
navigator.serial?.addEventListener("disconnect", (event) => {
  finalizeConnection(event.target).catch(() => {
  });
});
window.addEventListener("resize", resizeCanvas);
resizeCanvas();
function startReplay() {
  connectBtn.hidden = true;
  sampleRate.value = "50";
  sampleRate.disabled = true;
  resetCoachState();
  let sequence = 0;
  const started = performance.now();
  const timer = setInterval(() => {
    const elapsed = Math.trunc(performance.now() - started);
    const phase = elapsed % 833;
    let pulse = 0;
    if (phase < 55) pulse = phase * 21;
    else if (phase < 145) pulse = 1155 - (phase - 55) * 10;
    else if (phase < 205) pulse = 255 - (phase - 145) * 4;
    else if (phase < 245) pulse = (phase - 205) * 5;
    else if (phase < 315) pulse = 200 - (phase - 245) * 3;
    const signal = Math.max(0, Math.min(4095, 1880 + Math.max(0, pulse)));
    const frame = {
      source: "bench",
      format: "Browser bench pulse",
      version: 0,
      mode: "SIM",
      sequence,
      timestampMs: elapsed,
      signal,
      sourceBpm: null,
      sourceIbi: null,
      sourceBeat: null,
      sourceQuality: null,
      sourceState: null
    };
    updateFrame(frame, String(signal));
    sequence = sequence + 1 & 2147483647;
  }, 20);
  return timer;
}
if (pageOptions.get("bench") === "1") startReplay();
window.__PULSE_COACH_QA__ = {
  inject: (line, timestamp = null) => {
    const frame = parsePulseLine(line);
    if (frame) updateFrame(frame, line, timestamp);
    return { frame, coach: lastCoach };
  },
  reset: () => {
    browserCoach.reset();
    history = new Array(HISTORY_LENGTH).fill(null);
    frameCount = 0;
    gaps = 0;
    lastSequence = null;
    streamTimestamp = null;
    lastDeviceTimestamp = null;
  },
  getState: () => ({
    frameCount,
    gaps,
    lastFrame,
    coach: lastCoach,
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
    connected: Boolean(port),
    button: connectBtn.textContent,
    status: statusText.textContent,
    source: sourceMeta.textContent
  })
};
